import { Link } from 'react-router-dom';
import { Alert, Button, Col, Descriptions, Progress, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { CompanyTransfer, eldBrandLabel, TransferError } from '../../../../../../API/TT_ELD/companyTransfer';
import { eldTypeLabel, formatDate, TransferStatusTag } from './transferLabels';

const { Text } = Typography;

const STAGE_LABEL: Record<TransferError['stage'], string> = {
  logs: 'Logs',
  trackings: 'Tracking',
  company: 'Company',
  finish: 'Finish',
};

const errorColumns = [
  {
    title: 'Driver',
    dataIndex: 'driverId',
    key: 'driverId',
    render: (id?: number) => (id ? <Link to={`/user/users/${id}`}>#{id}</Link> : '-'),
  },
  {
    title: 'Stage',
    dataIndex: 'stage',
    key: 'stage',
    render: (stage: TransferError['stage']) => <Tag>{STAGE_LABEL[stage] ?? stage}</Tag>,
  },
  { title: 'Day', dataIndex: 'date', key: 'date', render: (date?: string) => date || '-' },
  { title: 'Message', dataIndex: 'message', key: 'message' },
];

interface Props {
  transfer: CompanyTransfer;
  canManage: boolean;
  retrying: boolean;
  onRetry(): void;
}

const TransferProgress = ({ transfer, canManage, retrying, onRetry }: Props) => {
  const total = transfer.driversTotal ?? 0;
  const done = transfer.driversDone ?? 0;
  const failed = transfer.driversFailed ?? 0;
  const percent = total ? Math.round(((done + failed) / total) * 100) : 100;
  const errors = transfer.errors ?? [];
  // Only a transfer that left this ELD can be retried from here
  const canRetry =
    canManage && transfer.direction === 'out' && (transfer.status === 'partial' || transfer.status === 'failed');

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
        <Descriptions.Item label="Direction">
          {transfer.direction === 'out' ? 'Out' : 'In'}: {eldBrandLabel(transfer.sourceEld)} →{' '}
          {eldBrandLabel(transfer.targetEld)}
        </Descriptions.Item>
        {transfer.direction === 'out' && (
          <Descriptions.Item label="Type on target">{eldTypeLabel(transfer.targetLegal) || '-'}</Descriptions.Item>
        )}
        <Descriptions.Item label="Status">
          <TransferStatusTag status={transfer.status} />
        </Descriptions.Item>
        <Descriptions.Item label="Started">{formatDate(transfer.startedAt)}</Descriptions.Item>
        <Descriptions.Item label="Finished">{formatDate(transfer.finishedAt)}</Descriptions.Item>
        {transfer.direction === 'out' && (
          <Descriptions.Item label="Tracking (24h)">{transfer.includeTracking ? 'Included' : 'Not included'}</Descriptions.Item>
        )}
        {transfer.mode === 'continue' && (
          <Descriptions.Item label="Mode">Continued the earlier record</Descriptions.Item>
        )}
        {transfer.remoteCompanyUid && (
          <Descriptions.Item label={transfer.direction === 'out' ? 'Company UID on target' : 'Company UID on source'}>
            <Text copyable>{transfer.remoteCompanyUid}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      {transfer.direction === 'out' && (
        <>
          <Progress
            percent={percent}
            success={{ percent: total ? (done / total) * 100 : 100 }}
            status={transfer.status === 'running' ? 'active' : failed ? 'exception' : 'success'}
          />
          <Row gutter={16}>
            <Col xs={8}>
              <Statistic title="Drivers" value={total} />
            </Col>
            <Col xs={8}>
              <Statistic title="Done" value={done} valueStyle={{ color: '#52c41a' }} />
            </Col>
            <Col xs={8}>
              <Statistic title="Failed" value={failed} valueStyle={{ color: failed ? '#ff4d4f' : undefined }} />
            </Col>
          </Row>
        </>
      )}

      {transfer.status === 'running' && (
        <Alert
          type="info"
          showIcon
          message="The transfer is running in the background. This page updates automatically."
        />
      )}
      {transfer.status === 'failed' && !transfer.remoteCompanyUid && (
        <Alert
          type="error"
          showIcon
          message="The target ELD did not accept the company"
          description="Nothing changed on this ELD. Fix the cause below and start a new transfer."
        />
      )}

      {errors.length > 0 && (
        <>
          <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
            <Text strong>Errors ({errors.length})</Text>
            {canRetry && (
              <Button icon={<ReloadOutlined />} loading={retrying} onClick={onRetry}>
                Retry failed drivers
              </Button>
            )}
          </Space>
          <Table
            size="small"
            rowKey={(error, index) => `${error.driverId ?? error.stage}-${error.date ?? 'all'}-${index}`}
            dataSource={errors}
            columns={errorColumns}
            pagination={errors.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
      {errors.length === 0 && canRetry && (
        <div>
          <Button icon={<ReloadOutlined />} loading={retrying} onClick={onRetry}>
            Retry
          </Button>
        </div>
      )}
    </Space>
  );
};

export default TransferProgress;
